import * as Sentry from "@sentry/browser";

import {
	CONFIG_ENVIRONMENT,
	CONFIG_SENTRY_DSN,
	CONFIG_SENTRY_DEBUG,
	CONFIG_SHA,
} from "./config";
import { errorSource } from "./sentry/error-source";
import { debug } from "./shared/debug";

export function Sentry_attach() {
	if (!CONFIG_SENTRY_DSN) {
		debug("sentry disabled: dsn missing");
		return;
	}

	if (!window) {
		debug("sentry: disabled: non-browser environment");
	}

	if (window.location.hostname === "localhost") {
		debug("sentry: disabled: localhost environment");
		return;
	}

	Sentry.init({
		dsn: CONFIG_SENTRY_DSN,
		release: CONFIG_SHA,
		debug: CONFIG_SENTRY_DEBUG,
		environment: CONFIG_ENVIRONMENT,
		enableLogs: true,
		integrations: [Sentry.browserTracingIntegration()],
		tracePropagationTargets: [
			/^https:\/\/global\.sdk\.myzeller\.(dev|show|com)\/graphql/,
		],
		maxBreadcrumbs: 30,
		sendDefaultPii: true,
		tracesSampleRate: 0.1 /* 10% */,
		beforeSend,
		beforeSendLog: beforeSendLog(),
		beforeBreadcrumb: beforeBreadcrumb(),
	});

	function beforeSendLog(): Sentry.BrowserOptions["beforeSendLog"] {
		const exclude = new Set<Sentry.LogSeverityLevel>(["debug"]);
		return (log) => (exclude.has(log.level) ? null : log);
	}
	function beforeBreadcrumb(): Sentry.BrowserOptions["beforeBreadcrumb"] {
		return (breadcrumb) => {
			// Ignore all `console.debug` breadcrumbs.
			const { level, category } = breadcrumb;
			if (level === "debug" && category === "console") {
				return null;
			}
			return breadcrumb;
		};
	}

	debug("sentry: ready");
}

export const beforeSend: NonNullable<Sentry.BrowserOptions["beforeSend"]> = (
	event,
) => {
	const source = errorSource(event);

	event.tags = { ...event.tags, "zeller.error.source": source };

	// Keep host-injected script errors out of our triage queue without
	// discarding them: the extra fingerprint component stops them merging
	// into an app issue, and they remain the evidence we hand to vendors.
	if (source === "external") {
		event.level = "warning";
		event.fingerprint = [
			...(event.fingerprint ?? ["{{ default }}"]),
			"external",
		];
	}

	return event;
};
